import { useEffect, useState } from "react";
import { Settings, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { TimeInput24 } from "./DateTime24";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { useAuth } from "../lib/auth";
import {
  fetchManualTowPricing,
  updateManualTowPricing,
} from "../lib/api";

/**
 * Gear-icon dialog on Tow Calc page.
 *
 * Everyone sees current rates; only admins/owners can save. AA & FR
 * pricing stay on Settings → Integrations (not editable here).
 *
 * Money fields are stored EXCLUSIVE of GST — GST is added on top in
 * the final quote. GST rate is persisted as 0-1 on the server but
 * surfaced as a 0-100% in the UI.
 */

// All money fields are ex-GST — GST is applied on top in `manual_tow_quote`.
const MONEY_FIELDS = [
  { k: "hourly_rate", label: "Hourly rate ($/hr, excl. GST)", step: "0.01", hint: "Charged over drive + load/unload time." },
  { k: "after_hours_surcharge", label: "After-hours surcharge ($, excl. GST)", step: "0.01", hint: "Flat $ added outside business hours." },
  { k: "faf_per_km", label: "FAF per km ($, excl. GST)", step: "0.001", hint: "Fuel Adjustment Factor charged on every road km." },
  { k: "per_km_rate", label: "Per-km rate ($, excl. GST)", step: "0.001", hint: "Optional extra on top of hourly (0 = ignore)." },
  { k: "minimum_charge", label: "Minimum charge ($, excl. GST)", step: "0.01", hint: "Safety floor. Effective minimum = max(1h × hourly rate, this value). Any after-hours surcharge is added on top." },
  { k: "heavy_vehicle_surcharge", label: "Heavy vehicle surcharge ($, excl. GST)", step: "0.01", hint: "Flat $ added when the Tow Calc 'Heavy vehicle' tick box is on. Admin fills in — defaults $0 (opt-in)." },
];

const _toHHMM = (h) => `${String(Number(h) || 0).padStart(2, "0")}:00`;
const _fromHHMM = (s) => {
  const [h] = String(s || "").split(":");
  return Number(h) || 0;
};

export default function ManualTowPricingDialog({ open, onOpenChange, onSaved }) {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchManualTowPricing()
      .then((raw) => setCfg(_decorate(raw)))
      .catch(() => toast.error("Failed to load Manual Tow pricing"));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        ...Object.fromEntries(MONEY_FIELDS.map((f) => [f.k, Number(cfg[f.k])])),
        load_unload_minutes: Number(cfg.load_unload_minutes),
        travel_time_correction_pct: Number(cfg.travel_time_correction_pct ?? 0),
        bill_time_rounding_minutes: Number(cfg.bill_time_rounding_minutes ?? 0),
        gst_rate: Number(cfg._gst_percent) / 100,
        business_hours_start_hour: _fromHHMM(cfg._business_start),
        business_hours_end_hour: _fromHHMM(cfg._business_end),
      };
      if (patch.gst_rate < 0 || patch.gst_rate > 1) {
        toast.error("GST % must be between 0 and 100");
        setSaving(false);
        return;
      }
      const next = await updateManualTowPricing(patch);
      setCfg(_decorate(next));
      toast.success("Manual Tow rates saved");
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Enrich the server payload with UI-shape fields (percent + HH:MM).
  function _decorate(raw) {
    return {
      ...raw,
      _gst_percent: Number((Number(raw.gst_rate ?? 0.15) * 100).toFixed(2)),
      _business_start: _toHHMM(raw.business_hours_start_hour ?? 8),
      _business_end: _toHHMM(raw.business_hours_end_hour ?? 17),
    };
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-pricing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-emerald-600" />
            Manual Tow Rates
          </DialogTitle>
          <DialogDescription>
            All money values are <strong>excl. GST</strong>. GST is added on top
            in the final quote. AA and First Rescue rate cards stay in Settings
            → Integrations (admin-only).
          </DialogDescription>
        </DialogHeader>

        {!cfg ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Loading manual tow pricing…
          </div>
        ) : (
          <>
            {!isAdmin && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/40 text-amber-800 dark:text-amber-200 p-3 text-xs flex items-center gap-2">
                <Lock className="h-3.5 w-3.5" />
                You can view these rates. Ask an admin to change them.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {MONEY_FIELDS.map((f) => (
                <div key={f.k}>
                  <Label htmlFor={`manual-${f.k}`} className="text-xs">
                    {f.label}
                  </Label>
                  <Input
                    id={`manual-${f.k}`}
                    type="number"
                    step={f.step}
                    value={cfg[f.k] ?? ""}
                    onChange={(e) =>
                      setCfg({ ...cfg, [f.k]: e.target.value })
                    }
                    disabled={!isAdmin}
                    data-testid={`manual-${f.k}`}
                  />
                  {f.hint && (
                    <p className="text-[11px] text-slate-400 mt-0.5">{f.hint}</p>
                  )}
                </div>
              ))}

              <div>
                <Label htmlFor="manual-load_unload_minutes" className="text-xs">
                  Load/unload (min)
                </Label>
                <Input
                  id="manual-load_unload_minutes"
                  type="number"
                  step="1"
                  value={cfg.load_unload_minutes ?? ""}
                  onChange={(e) =>
                    setCfg({ ...cfg, load_unload_minutes: e.target.value })
                  }
                  disabled={!isAdmin}
                  data-testid="manual-load_unload_minutes"
                />
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Added to drive time when billing hourly.
                </p>
              </div>

              <div>
                <Label htmlFor="manual-travel_time_correction_pct" className="text-xs">
                  Travel time correction (%)
                </Label>
                <Input
                  id="manual-travel_time_correction_pct"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={cfg.travel_time_correction_pct ?? ""}
                  onChange={(e) =>
                    setCfg({ ...cfg, travel_time_correction_pct: e.target.value })
                  }
                  disabled={!isAdmin}
                  data-testid="manual-travel_time_correction_pct"
                />
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Route times are free-flow car times. Add a % for traffic,
                  lights and a loaded truck. Drive time only — never km or FAF.
                </p>
              </div>

              <div>
                <Label htmlFor="manual-bill_time_rounding_minutes" className="text-xs">
                  Bill time in blocks of (min)
                </Label>
                <Input
                  id="manual-bill_time_rounding_minutes"
                  type="number"
                  step="1"
                  min="0"
                  max="60"
                  value={cfg.bill_time_rounding_minutes ?? ""}
                  onChange={(e) =>
                    setCfg({ ...cfg, bill_time_rounding_minutes: e.target.value })
                  }
                  disabled={!isAdmin}
                  data-testid="manual-bill_time_rounding_minutes"
                />
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Billable time rounds to the nearest block (0 = exact minutes).
                </p>
              </div>

              <div>
                <Label htmlFor="manual-gst_percent" className="text-xs">
                  GST rate (%)
                </Label>
                <Input
                  id="manual-gst_percent"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={cfg._gst_percent ?? ""}
                  onChange={(e) =>
                    setCfg({ ...cfg, _gst_percent: e.target.value })
                  }
                  disabled={!isAdmin}
                  data-testid="manual-gst_percent"
                />
                <p className="text-[11px] text-slate-400 mt-0.5">
                  NZ default is 15%.
                </p>
              </div>

              <div>
                <Label htmlFor="manual-business_start" className="text-xs">
                  Business hours start
                </Label>
                <TimeInput24
                  value={cfg._business_start ?? "08:00"}
                  onChange={(v) => setCfg({ ...cfg, _business_start: v })}
                  disabled={!isAdmin}
                  dataTestId="manual-business_start"
                />
              </div>

              <div>
                <Label htmlFor="manual-business_end" className="text-xs">
                  Business hours end
                </Label>
                <TimeInput24
                  value={cfg._business_end ?? "17:00"}
                  onChange={(v) => setCfg({ ...cfg, _business_end: v })}
                  disabled={!isAdmin}
                  dataTestId="manual-business_end"
                />
                <p className="text-[11px] text-slate-400 mt-0.5">
                  After-hours = outside this window. The on-call schedule
                  (Settings → On-call) is what actually drives the surcharge
                  on live quotes.
                </p>
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="manual-cancel-btn"
          >
            {isAdmin ? "Cancel" : "Close"}
          </Button>
          {isAdmin && (
            <Button onClick={save} disabled={saving || !cfg} data-testid="manual-save-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving…" : "Save rates"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
